import { useSuspenseQueries } from '@tanstack/react-query';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import { Button, Description, Heading } from '@gitbook/spine-react';

import { useSetAnalyticsScreen } from '../../navigation';
import { apiQuery, useLoggedInUser, useGetUpgradeAction } from '../../state';
import { UpgradeDialogProvider } from '../UpgradeDialog';
import { AccessWall } from './AccessWall';

/**
 * Placeholder blocking the application when the member is disabled.
 */
export function OrganizationAccessPlaceholder(props: {
    organization: string;
    children: React.ReactNode;
}): React.ReactElement {
    const { organization: organizationId, children } = props;

    const user = useLoggedInUser();
    const [{ data: organization }, { data: member }] = useSuspenseQueries({
        queries: [
            apiQuery('getOrganizationById', { organizationId }, { nullOnError: true }),
            apiQuery(
                'getMemberInOrganizationById',
                { organizationId, userId: user.uid },
                { nullOnError: true }
            ),
        ],
    });

    if (organization && member?.disabled) {
        return (
            <UpgradeDialogProvider>
                <DisabledPlaceholder organization={organization} />
            </UpgradeDialogProvider>
        );
    }

    return <>{children}</>;
}

function DisabledPlaceholder(props: { organization: api.Organization }): React.ReactElement {
    const { organization } = props;

    const [requested, setRequested] = React.useState(false);

    const upgrade = useGetUpgradeAction({
        organization,
        featureKey: api.OrganizationFeature.MaxMembers,
    });

    useSetAnalyticsScreen(
        organization
            ? analytics.createOrgEvent(organization.id, {
                  screen: 'organization_frozen',
              })
            : null,
        [organization?.id]
    );

    const onPressUpgrade = async () => {
        if (requested) {
            return;
        }

        await upgrade?.onPress();
        setRequested(true);
    };

    return (
        <AccessWall>
            <Heading className="mb-2 text-center text-ui-page-title">You're locked out</Heading>
            <Description className="mb-4 max-w-112 text-center">
                Your membership of the organization <strong>{organization?.title}</strong> has been
                suspended. Ask your administrator to upgrade to a paid plan.
            </Description>
            {requested ? (
                <Button
                    leadingIcon={Icon.Check}
                    kind="upgrade"
                    disabled
                    label="Upgrade requested"
                />
            ) : (
                <Button
                    testID="content.disabledUpgradeButton"
                    kind="upgrade"
                    leadingIcon={Icon.Upgrade}
                    onPress={onPressUpgrade}
                    label="Ask admin to upgrade"
                />
            )}
        </AccessWall>
    );
}
